import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { usePostEntityMedia, useDeleteEntityMedia } from '@/hooks'
import { MediaType, ModelsWithMedia, Photo, SoundRecord } from '@/types'
import { AxiosResponse } from 'axios'

type UseUploadMediaParams = {
  modelKey: ModelsWithMedia
}

export type ChosenFile = {
  file: Blob
  link: string
  type?: string
  description?: string
}

export type PatchMedia = {
  chosenToUpload: ChosenFile[]
  uploadedToApi: Photo[] | SoundRecord[]
  stale: number[]
  mediaType: MediaType
}

type UploadMediaParams = {
  media: PatchMedia
  entityId: number
  forSpecieNumber?: number
}

export const useUploadMedia = ({ modelKey }: UseUploadMediaParams) => {
  const { t } = useTranslation()

  const { enqueueSnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState(false)

  const postMedia = usePostEntityMedia({ modelKey })

  const deleteMedia = useDeleteEntityMedia({ modelKey })

  const uploadMedia = async ({
    media,
    entityId,
    forSpecieNumber,
  }: UploadMediaParams) => {
    setIsLoading(true)

    if (media && media.chosenToUpload) {
      const result = await postMedia({
        entityId,
        media,
        forSpecieNumber,
      })

      const failedIndexes = [] as string[]

      result.forEach((response, index) => {
        if (response.status === 'rejected' || response.value.status !== 200) {
          failedIndexes.push(`#${index + 1}`)

          return
        }
      })

      if (failedIndexes.length) {
        enqueueSnackbar(
          t('errors:api.uploadFailed', {
            indexes: failedIndexes.join(', '),
          }),
          {
            variant: 'error',
            persist: true,
          }
        )
      }
    }

    if (media.stale) {
      const mediaToDelete: Promise<AxiosResponse<void>>[] = []

      for (const id of media.stale) {
        mediaToDelete.push(
          deleteMedia({
            entityId,
            mediaId: id,
            mediaType: media.mediaType,
          })
        )
      }

      try {
        await Promise.all(mediaToDelete)
      } catch (e) {
        enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
          variant: 'error',
        })
      }
    }

    setIsLoading(false)
  }

  return { uploadMedia, isLoading }
}
